/**
 * Product Registry Service - CRUD operations for Product Registry
 * 
 * Provides database-backed product registry management with:
 * - Full CRUD for products
 * - Bulk import with upsert strategy
 * - CSV export functionality
 */

import { supabase } from "@/lib/supabase";
import { isUuid } from "@/lib/uuid";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { RegistryProduct, ProductCategory } from "@/data/ProductRegistryContext";

export interface ProductRegistryRow {
  id: string;
  product_id: string;
  product_name: string;
  sku: string;
  vendor: string;
  category: string;
  subcategory: string | null;
  unit_of_measure: string;
  cost: number;
  price: number;
  track_inventory: boolean;
  stock_quantity: number;
  reorder_threshold: number;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface EnsureRegistryMaterialInput {
  name: string;
  assignedSku?: string;
  vendorName?: string;
  category?: ProductCategory;
  subcategory?: string;
  unitCost?: number;
  unitPrice?: number;
}

export type VendorSkuMatchType = "exact" | "variant" | "manual_override" | "inferred";

export interface ProductVendorSkuMapRow {
  id: string;
  vendor_id: string;
  vendor_name?: string | null;
  vendor_sku: string;
  internal_sku: string;
  variant_key: string | null;
  uom: string | null;
  pack_size: number | null;
  match_type: VendorSkuMatchType;
  confidence: number | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductVendorSkuMapInput {
  id?: string;
  vendorId: string;
  vendorSku: string;
  internalSku?: string;
  variantKey?: string | null;
  uom?: string | null;
  packSize?: number | null;
  matchType?: VendorSkuMatchType;
  confidence?: number | null;
  isActive?: boolean;
  notes?: string | null;
}

export type InventoryCsvImportMode = "add" | "overwrite";

export interface InventoryCsvImportAuditChange {
  importId: string;
  fileName: string;
  mode: InventoryCsvImportMode;
  sku: string;
  productName: string;
  previousQuantity: number;
  importedQuantity: number;
  nextQuantity: number;
  action: "created" | "updated";
  rowNumber?: number;
}

function rowToProduct(row: ProductRegistryRow): RegistryProduct {
  return {
    productId: row.product_id,
    productName: row.product_name,
    sku: row.sku,
    vendor: row.vendor,
    category: row.category as ProductCategory,
    subcategory: row.subcategory || "",
    unitOfMeasure: row.unit_of_measure,
    cost: Number(row.cost),
    price: Number(row.price),
    trackInventory: row.track_inventory,
    stockQuantity: row.stock_quantity,
    reorderThreshold: row.reorder_threshold,
    active: row.is_active,
    notes: row.notes || "",
  };
}

function productToRow(product: Partial<RegistryProduct>): Partial<ProductRegistryRow> {
  return {
    product_id: product.productId,
    product_name: product.productName,
    sku: product.sku,
    vendor: product.vendor,
    category: product.category,
    subcategory: product.subcategory || null,
    unit_of_measure: product.unitOfMeasure || "each",
    cost: product.cost,
    price: product.price,
    track_inventory: product.trackInventory || false,
    stock_quantity: product.stockQuantity || 0,
    reorder_threshold: product.reorderThreshold || 0,
    is_active: product.active !== false,
    notes: product.notes || null,
  };
}

export async function getProducts(): Promise<RegistryProduct[]> {
  const PAGE_SIZE = 1000;
  const allRows: ProductRegistryRow[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("product_registry")
      .select("*")
      .order("product_name")
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      console.error("Failed to fetch products:", error);
      throw error;
    }

    allRows.push(...(data as ProductRegistryRow[]));
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return allRows.map(rowToProduct);
}

export async function getProductsForSkuMatching(): Promise<RegistryProduct[]> {
  const PAGE_SIZE = 1000;
  const allRows: Array<
    Pick<
      ProductRegistryRow,
      | "product_id"
      | "product_name"
      | "sku"
      | "vendor"
      | "category"
      | "subcategory"
      | "unit_of_measure"
      | "cost"
      | "price"
      | "track_inventory"
      | "stock_quantity"
      | "reorder_threshold"
      | "is_active"
      | "notes"
    >
  > = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from("product_registry")
      .select(
        [
          "product_id",
          "product_name",
          "sku",
          "vendor",
          "category",
          "subcategory",
          "unit_of_measure",
          "cost",
          "price",
          "track_inventory",
          "stock_quantity",
          "reorder_threshold",
          "is_active",
          "notes",
        ].join(","),
      )
      .eq("is_active", true)
      .order("product_name")
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      console.error("Failed to fetch product registry SKU matching catalog:", error);
      throw error;
    }

    const rows = (data || []) as typeof allRows;
    allRows.push(...rows);
    if (rows.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return allRows.map((row) =>
    rowToProduct({
      id: "",
      created_at: "",
      updated_at: "",
      ...row,
    }),
  );
}

export async function createProduct(product: RegistryProduct): Promise<void> {
  const orgId = await getCurrentOrganizationId();
  const row = productToRow(product);

  // Check if a product with this product_id already exists
  const existingByProductId = isUuid(product.productId)
    ? await supabase
        .from("product_registry")
        .select("id")
        .eq("product_id", product.productId)
        .maybeSingle()
    : { data: null, error: null };
  const { data: existing, error: checkError } = existingByProductId;

  if (checkError) {
    console.error("Error checking for existing product:", checkError);
  } else if (existing) {
    const error = new Error("Product with this ID already exists") as any;
    error.code = "23505";
    error.constraint = "product_registry_product_id_key";
    throw error;
  }

  // Also check for duplicate SKU
  const { data: existingSku, error: skuCheckError } = await supabase
    .from("product_registry")
    .select("id")
    .eq("sku", product.sku)
    .maybeSingle();

  if (skuCheckError) {
    console.error("Error checking for existing SKU:", skuCheckError);
  } else if (existingSku) {
    const error = new Error("Product with this SKU already exists") as any;
    error.code = "23505";
    error.constraint = "product_registry_sku_key";
    throw error;
  }

  const { error } = await supabase.from("product_registry").insert({ ...row, organization_id: orgId });

  if (error) {
    console.error("Failed to create product:", error);
    throw error;
  }
}

function normalizeSkuToken(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

function categoryPrefix(category: ProductCategory): string {
  switch (category) {
    case "Wall System":
      return "WALL";
    case "Base":
      return "BASE";
    case "Glass/Enclosure":
      return "GLASS";
    case "Hardware":
      return "HARD";
    case "Add-On":
      return "ADD";
    default:
      return "OTH";
  }
}

function inferCategoryFromName(name: string): ProductCategory {
  const lookup = name.toLowerCase();
  if (lookup.includes("door") || lookup.includes("glass") || lookup.includes("enclosure")) return "Glass/Enclosure";
  if (lookup.includes("wall")) return "Wall System";
  if (lookup.includes("base") || lookup.includes("pan")) return "Base";
  if (
    lookup.includes("seat")
    || lookup.includes("bench")
    || lookup.includes("corner")
    || lookup.includes("niche")
    || lookup.includes("shelf")
    || lookup.includes("caddy")
    || lookup.includes("add-on")
    || lookup.includes("addon")
  ) {
    return "Add-On";
  }
  return "Other";
}

function deterministicSku(name: string, category: ProductCategory): string {
  const normalized = normalizeSkuToken(name);
  const prefix = categoryPrefix(category);
  return `${prefix}-AUTO-${normalized || "ITEM"}`;
}

function buildVariantInternalSku(vendorSku: string, variantKey: string): string {
  const base = normalizeSkuToken(vendorSku);
  const variant = normalizeSkuToken(variantKey);
  if (!base || !variant) return "";
  return `${base}-${variant}`;
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function normalizeAssignedSku(value: string | undefined): string | null {
  const raw = String(value || "").trim();
  if (!raw || raw === "—" || isUuidLike(raw)) return null;
  return normalizeSkuToken(raw);
}

export async function ensureProductsForMaterialNames(
  materials: EnsureRegistryMaterialInput[],
): Promise<{ created: number; ensured: Array<{ name: string; sku: string; category: ProductCategory }> }> {
  const normalized = materials
    .map((m) => ({ ...m, name: (m.name || "").trim() }))
    .filter((m) => m.name.length > 0);

  if (normalized.length === 0) return { created: 0, ensured: [] };

  const existing = await getProducts();
  const existingByName = new Map(existing.map((p) => [p.productName.trim().toLowerCase(), p]));
  const existingBySku = new Map(existing.map((p) => [p.sku.trim().toUpperCase(), p]));

  let created = 0;
  const ensured: Array<{ name: string; sku: string; category: ProductCategory }> = [];

  for (const material of normalized) {
    const byName = existingByName.get(material.name.toLowerCase());
    if (byName) {
      ensured.push({ name: byName.productName, sku: byName.sku, category: byName.category });
      continue;
    }

    const category = material.category || inferCategoryFromName(material.name);
    const assignedSku = normalizeAssignedSku(material.assignedSku);
    const sku = assignedSku || deterministicSku(material.name, category);
    const bySku = existingBySku.get(sku.toUpperCase());
    if (bySku) {
      ensured.push({ name: bySku.productName, sku: bySku.sku, category: bySku.category });
      continue;
    }

    const product: RegistryProduct = {
      productId: crypto.randomUUID(),
      productName: material.name,
      sku,
      vendor: String(material.vendorName || "").trim() || "Auto-Mapped",
      category,
      subcategory: material.subcategory || "Auto-Mapped",
      unitOfMeasure: "each",
      cost: Number(material.unitCost || 0),
      price: Number(material.unitPrice || material.unitCost || 0),
      trackInventory: true,
      stockQuantity: 0,
      reorderThreshold: 1,
      active: true,
      notes: "Auto-created from consultation/build review unmapped material.",
    };

    await createProduct(product);
    existingByName.set(product.productName.toLowerCase(), product);
    existingBySku.set(product.sku.toUpperCase(), product);
    ensured.push({ name: product.productName, sku: product.sku, category: product.category });
    created += 1;
  }

  return { created, ensured };
}

function toVendorSkuMapInputRow(input: ProductVendorSkuMapInput) {
  const normalizedVendorSku = input.vendorSku.trim();
  const normalizedVariantKey = (input.variantKey ?? "").trim();
  const normalizedMatchType = input.matchType ?? "exact";
  const maybeAutoInternalSku =
    !String(input.internalSku || "").trim() && normalizedVariantKey
      ? buildVariantInternalSku(normalizedVendorSku, normalizedVariantKey)
      : "";
  const normalizedInternalSku = (String(input.internalSku || "").trim() || maybeAutoInternalSku).trim();

  if (!normalizedInternalSku) {
    throw new Error("internalSku is required (or provide variantKey to auto-generate one).");
  }
  if (normalizedMatchType === "variant" && !normalizedVariantKey) {
    throw new Error("variantKey is required when matchType is 'variant'.");
  }

  return {
    vendor_id: input.vendorId,
    vendor_sku: normalizedVendorSku,
    internal_sku: normalizedInternalSku,
    variant_key: normalizedVariantKey || null,
    uom: input.uom ?? null,
    pack_size: input.packSize ?? null,
    match_type: normalizedMatchType,
    confidence: input.confidence ?? null,
    is_active: input.isActive ?? true,
    notes: input.notes ?? null,
  };
}

export async function getVendorSkuMappings(vendorId?: string): Promise<ProductVendorSkuMapRow[]> {
  let query = supabase
    .from("product_vendor_sku_map")
    .select("*, catalog_vendors(name)")
    .order("vendor_sku", { ascending: true })
    .order("internal_sku", { ascending: true });

  if (vendorId) {
    query = query.eq("vendor_id", vendorId);
  }

  const { data, error } = await query;
  if (error) {
    console.error("Failed to fetch vendor SKU mappings:", error);
    throw error;
  }

  return (data || []).map((row) => {
    const vendorRow = row as ProductVendorSkuMapRow & {
      catalog_vendors?: { name?: string | null } | Array<{ name?: string | null }> | null;
    };
    const relatedVendor = Array.isArray(vendorRow.catalog_vendors)
      ? vendorRow.catalog_vendors[0]
      : vendorRow.catalog_vendors;
    return {
      ...vendorRow,
      vendor_name: relatedVendor?.name || vendorRow.vendor_name || null,
    };
  }) as ProductVendorSkuMapRow[];
}

export async function upsertVendorSkuMapping(input: ProductVendorSkuMapInput): Promise<void> {
  const row = toVendorSkuMapInputRow(input);

  if (input.id?.trim()) {
    const { error: updateByIdError } = await supabase
      .from("product_vendor_sku_map")
      .update(row)
      .eq("id", input.id.trim());

    if (updateByIdError) {
      console.error("Failed to update vendor SKU mapping by id:", updateByIdError);
      throw updateByIdError;
    }
    return;
  }

  // Do not rely on `onConflict` here because DB uniqueness is currently
  // expression-based (upper(vendor_sku), upper(internal_sku)), which PostgREST
  // cannot target via onConflict column list.
  const { data: existing, error: findError } = await supabase
    .from("product_vendor_sku_map")
    .select("id")
    .eq("vendor_id", row.vendor_id)
    .ilike("vendor_sku", row.vendor_sku)
    .ilike("internal_sku", row.internal_sku)
    .maybeSingle();

  if (findError) {
    console.error("Failed to find existing vendor SKU mapping:", findError);
    throw findError;
  }

  if (existing?.id) {
    const { error: updateError } = await supabase
      .from("product_vendor_sku_map")
      .update({
        variant_key: row.variant_key,
        uom: row.uom,
        pack_size: row.pack_size,
        match_type: row.match_type,
        confidence: row.confidence,
        is_active: row.is_active,
        notes: row.notes,
      })
      .eq("id", existing.id);

    if (updateError) {
      console.error("Failed to update vendor SKU mapping:", updateError);
      throw updateError;
    }
    return;
  }

  const { error: insertError } = await supabase
    .from("product_vendor_sku_map")
    .insert(row);

  if (insertError) {
    console.error("Failed to insert vendor SKU mapping:", insertError);
    throw insertError;
  }
}

export async function deleteVendorSkuMapping(id: string): Promise<void> {
  const { error } = await supabase
    .from("product_vendor_sku_map")
    .delete()
    .eq("id", id);
  if (error) {
    console.error("Failed to delete vendor SKU mapping:", error);
    throw error;
  }
}

export async function resolveInternalSkuFromVendorSku(
  vendorId: string,
  vendorSku: string,
): Promise<ProductVendorSkuMapRow[]> {
  const normalizedVendorSku = vendorSku.trim();
  if (!normalizedVendorSku) return [];

  const { data, error } = await supabase
    .from("product_vendor_sku_map")
    .select("*")
    .eq("vendor_id", vendorId)
    .eq("vendor_sku", normalizedVendorSku)
    .eq("is_active", true)
    .order("match_type", { ascending: true })
    .order("confidence", { ascending: false });

  if (error) {
    console.error("Failed to resolve internal SKU from vendor SKU:", error);
    throw error;
  }

  return (data || []) as ProductVendorSkuMapRow[];
}

export async function updateProduct(
  productId: string,
  product: Partial<RegistryProduct>
): Promise<void> {
  const row = productToRow(product);
  const updateQuery = supabase.from("product_registry").update(row);
  const { error } = isUuid(productId)
    ? await updateQuery.eq("product_id", productId)
    : await updateQuery.eq("sku", String(product.sku ?? "").trim());

  if (error) {
    console.error("Failed to update product:", error);
    throw error;
  }
}

export async function updateProductStockBySku(
  sku: string,
  stockQuantity: number,
): Promise<void> {
  const { error } = await supabase
    .from("product_registry")
    .update({
      stock_quantity: stockQuantity,
      track_inventory: true,
      updated_at: new Date().toISOString(),
    })
    .eq("sku", sku);

  if (error) {
    console.error("Failed to update product stock:", error);
    throw error;
  }
}

export async function logInventoryCsvImportAudit(input: {
  importId: string;
  fileName: string;
  mode: InventoryCsvImportMode;
  totalRows: number;
  changedRows: number;
  invalidRows: number;
  changes: InventoryCsvImportAuditChange[];
}): Promise<void> {
  if (input.changedRows === 0 && input.invalidRows === 0) return;

  const organizationId = await getCurrentOrganizationId();
  const now = new Date().toISOString();
  const summaryEvent = {
    organization_id: organizationId,
    event_type: "admin_override",
    job_id: null,
    triggered_by: "Warehouse",
    payload: {
      domain: "warehouse_inventory_csv_import",
      action: "inventory_csv_import_applied",
      importId: input.importId,
      fileName: input.fileName,
      mode: input.mode,
      totalRows: input.totalRows,
      changedRows: input.changedRows,
      invalidRows: input.invalidRows,
    },
    created_at: now,
  };

  const changeEvents = input.changes.map((change) => ({
    organization_id: organizationId,
    event_type: "admin_override",
    job_id: null,
    triggered_by: "Warehouse",
    payload: {
      domain: "warehouse_inventory_csv_import",
      action: "inventory_stock_changed",
      importId: change.importId,
      fileName: change.fileName,
      mode: change.mode,
      sku: change.sku,
      productName: change.productName,
      previousQuantity: change.previousQuantity,
      importedQuantity: change.importedQuantity,
      nextQuantity: change.nextQuantity,
      delta: change.nextQuantity - change.previousQuantity,
      changeType: change.action,
      rowNumber: change.rowNumber ?? null,
    },
    created_at: now,
  }));

  const { error } = await supabase
    .from("system_events")
    .insert([summaryEvent, ...changeEvents]);

  if (error) {
    console.error("Failed to write inventory CSV import audit:", error);
    throw error;
  }
}

async function upsertProducts(
  products: RegistryProduct[],
  mode: "overwrite" | "skip" | "merge"
): Promise<{ inserted: number; updated: number; skipped: number }> {
  const orgId = await getCurrentOrganizationId();
  const existing = await getProducts();
  const existingSkus = new Set(existing.map((p) => p.sku));

  let inserted = 0;
  let updated = 0;
  let skipped = 0;

  for (const product of products) {
    const row = productToRow(product);

    if (existingSkus.has(product.sku)) {
      if (mode === "overwrite") {
        const updateQuery = supabase
          .from("product_registry")
          .update(row);
        const { error } = isUuid(product.productId)
          ? await updateQuery.eq("product_id", product.productId)
          : await updateQuery.eq("sku", product.sku);
        if (!error) updated++;
      } else if (mode === "merge") {
        const existingProduct = existing.find((p) => p.sku === product.sku);
        if (existingProduct) {
          const mergedRow = productToRow({
            ...existingProduct,
            ...product,
            productId: existingProduct.productId,
          });
          const updateQuery = supabase
            .from("product_registry")
            .update(mergedRow);
          const { error } = isUuid(existingProduct.productId)
            ? await updateQuery.eq("product_id", existingProduct.productId)
            : await updateQuery.eq("sku", existingProduct.sku);
          if (!error) updated++;
        }
      } else {
        skipped++;
      }
    } else {
      const { error } = await supabase.from("product_registry").insert({ ...row, organization_id: orgId });
      if (!error) inserted++;
    }
  }

  return { inserted, updated, skipped };
}
  

export async function bulkImportProducts(
  products: RegistryProduct[],
  mode: "overwrite" | "skip" | "merge" = "skip"
): Promise<{ inserted: number; updated: number; skipped: number }> {
  return upsertProducts(products, mode);
}

export async function exportProductsToCsv(products: RegistryProduct[]): Promise<string> {
  const headers = [
    "productName",
    "sku",
    "vendor",
    "category",
    "subcategory",
    "unitOfMeasure",
    "cost",
    "price",
    "trackInventory",
    "stockQuantity",
    "reorderThreshold",
    "active",
    "notes",
  ];

  const csvRows = [
    headers.join(","),
    ...products.map((p) =>
      headers
        .map((h) => {
          const value = (p as any)[h];
          if (typeof value === "string") {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return String(value ?? "");
        })
        .join(",")
    ),
  ];

  return csvRows.join("\n");
}
